const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3003').replace(/\/$/, '');
import { getStoredToken } from './auth';

export interface ProfileUser {
  id: string;
  name: string;
  email?: string | null;
  avatar_url?: string | null;
  display_name?: string | null;
  show_avatar_on_stage?: boolean;
}

export interface SongHistoryItem {
  id: string;
  user_id: string;
  room_id: string;
  video_id: string;
  title: string;
  thumbnail?: string | null;
  played_at: string;
}

export interface FavoriteSong {
  id: string;
  user_id: string;
  video_id: string;
  title: string;
  thumbnail?: string | null;
  added_at: string;
}

export interface UserEvent {
  id: string;
  user_id: string;
  room_id: string;
  room_name?: string | null;
  event_date?: string | null;
  songs_sung: number;
  reactions_received: number;
  ended_at: string;
}

export interface UserProfile {
  user: ProfileUser;
  history: SongHistoryItem[];
  favorites: FavoriteSong[];
  events: UserEvent[];
}

async function authFetch(path: string): Promise<any> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function fetchUserProfile(userId: string): Promise<UserProfile> {
  const [profile, events] = await Promise.all([
    authFetch(`/api/users/${encodeURIComponent(userId)}/profile`),
    authFetch(`/api/users/${encodeURIComponent(userId)}/events`),
  ]);
  return {
    user: profile.user || { id: userId, name: 'Unknown User' },
    history: profile.history || [],
    favorites: profile.favorites || [],
    events: events.events || [],
  };
}

export interface StagePrefs {
  displayName: string;
  showAvatarOnStage: boolean;
}

export async function fetchStagePrefs(userId: string): Promise<StagePrefs | null> {
  try {
    const profile = await authFetch(`/api/users/${encodeURIComponent(userId)}/profile`);
    const user = profile.user;
    if (!user) return null;
    return {
      displayName: user.display_name || '',
      showAvatarOnStage: user.show_avatar_on_stage !== false,
    };
  } catch {
    return null;
  }
}

export async function updateStagePrefs(
  userId: string,
  prefs: { displayName?: string | null; showAvatarOnStage?: boolean }
): Promise<void> {
  const token = getStoredToken();
  const res = await fetch(`${API_URL}/api/users/${encodeURIComponent(userId)}/profile-settings`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(prefs),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
}
